const readEnv = (name: string): string => (process.env[name] || '').trim();

const SCHEMA_UID_PATTERN = /^0x[a-fA-F0-9]{64}$/;

export interface PublicRuntimeConfig {
  supabaseUrl: string;
  supabaseAnonKey: string;
  reownProjectId: string;
  attestationRelayerUrl: string;
  agreementSchemaUID: string;
  encryptedKycSchemaUID: string;
  identitySchemaUID: string;
  propertySchemaUID: string;
  questionnaireSchemaUID: string;
  easContractAddress: string;
  fileverseAgreementUrl: string;
  fileverseAgreementCID: string;
  fileverseApiUrl: string;
  fileverseApiKey: string;
  fileverseDdocId: string;
  primaryAdminAddress: string;
  buanFundPublicKeyHex: string;
  adminDecryptionUrl: string;
  adminApiUrl: string;
  ensRpcUrl: string;
  web3FormsAccessKey: string;
}

export const isSchemaUID = (value: string): boolean => SCHEMA_UID_PATTERN.test(value.trim());

interface ValidateRequiredPublicRuntimeConfigOptions {
  requireAttestationSchemas?: boolean;
}

export const getPublicRuntimeConfig = (): PublicRuntimeConfig => ({
  supabaseUrl: readEnv('REACT_APP_SUPABASE_URL'),
  supabaseAnonKey: readEnv('REACT_APP_SUPABASE_ANON_KEY'),
  reownProjectId: readEnv('REACT_APP_REOWN_PROJECT_ID'),
  attestationRelayerUrl: readEnv('REACT_APP_ATTESTATION_RELAYER_URL'),
  agreementSchemaUID: readEnv('REACT_APP_AGREEMENT_SCHEMA_UID'),
  encryptedKycSchemaUID: readEnv('REACT_APP_ENCRYPTED_KYC_SCHEMA_UID'),
  identitySchemaUID: readEnv('REACT_APP_IDENTITY_SCHEMA_UID'),
  propertySchemaUID: readEnv('REACT_APP_PROPERTY_SCHEMA_UID'),
  questionnaireSchemaUID: readEnv('REACT_APP_QUESTIONNAIRE_SCHEMA_UID'),
  easContractAddress: readEnv('REACT_APP_EAS_CONTRACT_ADDRESS'),
  fileverseAgreementUrl: readEnv('REACT_APP_FILEVERSE_AGREEMENT_URL'),
  fileverseAgreementCID: readEnv('REACT_APP_FILEVERSE_AGREEMENT_CID'),
  fileverseApiUrl: readEnv('REACT_APP_FILEVERSE_API_URL'),
  fileverseApiKey: readEnv('REACT_APP_FILEVERSE_API_KEY'),
  fileverseDdocId: readEnv('REACT_APP_FILEVERSE_DDOC_ID'),
  primaryAdminAddress: readEnv('REACT_APP_PRIMARY_ADMIN_ADDRESS'),
  buanFundPublicKeyHex: readEnv('REACT_APP_BUAN_FUND_PUBLIC_KEY_HEX'),
  adminDecryptionUrl: readEnv('REACT_APP_ADMIN_DECRYPTION_URL'),
  adminApiUrl: readEnv('REACT_APP_ADMIN_API_URL'),
  ensRpcUrl: readEnv('REACT_APP_ENS_RPC_URL'),
  web3FormsAccessKey: readEnv('REACT_APP_WEB3FORMS_ACCESS_KEY'),
});

const assertPresent = (name: string, value: string, guidance: string): void => {
  if (!value) {
    throw new Error(`Missing required environment variable: ${name}. ${guidance}`);
  }
};

const assertSchemaUIDIfPresent = (name: string, value: string): void => {
  if (value && !isSchemaUID(value)) {
    throw new Error(`Environment variable ${name} must be a 66-character hex schema UID (0x + 64 hex chars).`);
  }
};

const assertReadOnlyFileverseKey = (name: string, value: string): void => {
  // Client-side Fileverse keys must be read-only. Use server-side credentials for writes.
  if (value && !value.startsWith('ro_')) {
    throw new Error(
      `${name} must be a read-only Fileverse key (expected prefix: ro_). Move write-scoped keys to a trusted backend.`
    );
  }
};

const assertOneSchemaConfigured = (
  preferredName: string,
  preferredValue: string,
  legacyName: string,
  legacyValue: string
): void => {
  if (!preferredValue && !legacyValue) {
    throw new Error(
      `Missing required schema configuration. Set ${preferredName} or ${legacyName} before using the attestation flow.`
    );
  }

  assertSchemaUIDIfPresent(preferredName, preferredValue);
  assertSchemaUIDIfPresent(legacyName, legacyValue);
};

export const validateRequiredPublicRuntimeConfig = (
  options: ValidateRequiredPublicRuntimeConfigOptions = {}
): void => {
  const { requireAttestationSchemas = true } = options;
  const config = getPublicRuntimeConfig();

  assertPresent(
    'REACT_APP_SUPABASE_URL',
    config.supabaseUrl,
    'Set it in .env.local so the frontend can reach Supabase.'
  );
  assertPresent(
    'REACT_APP_SUPABASE_ANON_KEY',
    config.supabaseAnonKey,
    'Set it in .env.local so the frontend can authenticate to Supabase.'
  );

  if (requireAttestationSchemas) {
    assertOneSchemaConfigured(
      'REACT_APP_IDENTITY_SCHEMA_UID',
      config.identitySchemaUID,
      'REACT_APP_ENCRYPTED_KYC_SCHEMA_UID',
      config.encryptedKycSchemaUID
    );
    assertOneSchemaConfigured(
      'REACT_APP_PROPERTY_SCHEMA_UID',
      config.propertySchemaUID,
      'REACT_APP_ENCRYPTED_KYC_SCHEMA_UID',
      config.encryptedKycSchemaUID
    );
    assertOneSchemaConfigured(
      'REACT_APP_QUESTIONNAIRE_SCHEMA_UID',
      config.questionnaireSchemaUID,
      'REACT_APP_ENCRYPTED_KYC_SCHEMA_UID',
      config.encryptedKycSchemaUID
    );
  }
};

export const validateAgreementSubmissionRuntimeConfig = (): void => {
  const config = getPublicRuntimeConfig();

  assertPresent(
    'REACT_APP_AGREEMENT_SCHEMA_UID',
    config.agreementSchemaUID,
    'Set it in .env.local and restart the dev server before signing the agreement.'
  );
  assertSchemaUIDIfPresent('REACT_APP_AGREEMENT_SCHEMA_UID', config.agreementSchemaUID);

  if (!config.fileverseAgreementUrl && !config.fileverseAgreementCID) {
    throw new Error(
      'Missing Fileverse agreement reference. Set REACT_APP_FILEVERSE_AGREEMENT_URL or REACT_APP_FILEVERSE_AGREEMENT_CID before signing.'
    );
  }

  const hasAnyLiveFileverseConfig = Boolean(
    config.fileverseApiUrl || config.fileverseApiKey || config.fileverseDdocId
  );

  if (hasAnyLiveFileverseConfig) {
    assertPresent(
      'REACT_APP_FILEVERSE_API_URL',
      config.fileverseApiUrl,
      'It is required when live Fileverse resolution is enabled.'
    );
    assertPresent(
      'REACT_APP_FILEVERSE_API_KEY',
      config.fileverseApiKey,
      'It is required when live Fileverse resolution is enabled.'
    );
    assertReadOnlyFileverseKey('REACT_APP_FILEVERSE_API_KEY', config.fileverseApiKey);
    if (!config.fileverseDdocId && !config.fileverseAgreementUrl) {
      throw new Error(
        'Live Fileverse resolution requires REACT_APP_FILEVERSE_DDOC_ID or a parseable REACT_APP_FILEVERSE_AGREEMENT_URL.'
      );
    }
  }
};